import type { Except, Server } from '@festina-life/core'
import {
  type MutationFunction,
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { useAuthenticationContext } from '../context/authenticationContext'
import { useGetUserRoles } from './useGetUserRoles'

type Props<TData, TVariables> = {
  component: Server
  roles: readonly string[]
  isScenario: boolean
  mutateFunction: MutationFunction<TData, TVariables>
  options?: UseMutationOptions<TData, Error, TVariables, unknown>
}

export const useFestinaMutation = <TData, TVariables>({
  component,
  mutateFunction,
  roles,
  isScenario,
  options,
}: Props<TData, TVariables>): Except<
  UseMutationResult<TData, Error, TVariables, unknown>,
  'mutate'
> & {
  authorization: {
    isUserAllowedToDoAction: boolean
    roles: readonly string[]
  }
} => {
  const { data: userRoles } = useGetUserRoles()
  const { shouldValidateAuthenticationHooks } = useAuthenticationContext()
  const queryClient = useQueryClient()

  // If we are in scenario mode, we should only check the simulation roles with the .S suffix
  const rolesToCheck = isScenario
    ? roles.filter((role) => role.endsWith('.S'))
    : roles

  const isUserAllowedToDoAction = shouldValidateAuthenticationHooks
    ? rolesToCheck.every((role) => userRoles?.[component]?.includes(role))
    : true

  const { mutate: unusedMutate, ...remainingResponse } = useMutation({
    mutationFn: mutateFunction,
    ...options,
    onSuccess: async (...args) => {
      // https://tanstack.com/query/latest/docs/framework/react/guides/invalidations-from-mutations
      // recommends that the query invalidation is awaited, to make sure data is updated before the
      // mutation is entirely complete. However, this stalls the application for far too long after
      // a command submission. So, we prefer to fire the invalidation in the background and let the
      // suspense of individual components handle it themselves.
      void queryClient.invalidateQueries()
      await options?.onSuccess?.(...args)
    },
  })

  const handleUnauthorizedAccess = useCallback(() => {
    if (!isUserAllowedToDoAction) {
      throw new Error(
        // Could perhaps be nice to be able to know "what" the user is missing,
        // to let them know they need to ask for XYZ
        'You do not have sufficient access rights to perform this action.',
      )
    }

    return Promise.resolve()
    // Is casted in order for the trigger to be the same type as the original trigger
  }, [
    isUserAllowedToDoAction,
  ]) as unknown as typeof remainingResponse.mutateAsync

  return {
    ...remainingResponse,
    mutateAsync: (isUserAllowedToDoAction
      ? remainingResponse.mutateAsync
      : handleUnauthorizedAccess) as typeof remainingResponse.mutateAsync,
    authorization: { isUserAllowedToDoAction, roles },
  }
}
